import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useModal } from 'hooks/useModal';
import { FormCheckbox } from 'components/form';
import makeStyles from '@mui/styles/makeStyles';
import { blue, grey, red } from '@mui/material/colors';
import { ActionsMenu } from 'components/shared/Menu/ActionsMenu';
import { ModalsKeys, Nullable, QueryListResponse } from 'global';
import { Add, MoreHoriz, Delete, Edit } from '@mui/icons-material';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { CalendarViewConfigurationViewModel } from '@trnsact/trnsact-shared-types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { calendarApi, useCalendar } from '../../../api';
import { DealActivityAPINameSpace } from '../../../../api/types';
import { OtherCalendarsSettings } from './OtherCalendarsSettings';

export const OtherCalendars = () => {
  const classes = useStyles();

  const { handleOpen } = useModal(ModalsKeys.UpdateCalendarViewDialog);

  const anchorRef = useRef<Nullable<HTMLButtonElement>>(null);
  const [showMore, setShowMore] = useState<Nullable<HTMLElement>>(null);

  const { handleDeleteCalendarView, isDeletingCalendarView, handleToggleView, isToggingView } = useCalendar();

  const { error, loading, data, previousData } = useQuery<
    DealActivityAPINameSpace<QueryListResponse<CalendarViewConfigurationViewModel>>
  >(calendarApi.sharing.queries.configuration);

  const configList = (data ?? previousData)?.dealActivity?.result?.list ?? [];

  const handleOpenConfig = () => {
    setShowMore(anchorRef.current);
  };

  const handleCloseConfig = () => {
    setShowMore(null);
  };

  return (
    <Stack className={classes.container}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" color="text.secondary">
          Other Calendars
        </Typography>

        <IconButton
          size="small"
          color="primary"
          ref={anchorRef}
          onClick={handleOpenConfig}
          className="icon-btn-bordered"
        >
          <Add fontSize="small" />
        </IconButton>

        <OtherCalendarsSettings anchorEl={showMore} onClose={handleCloseConfig} />
      </Stack>

      <RenderWithFallback
        error={error}
        isLoading={loading}
        fallBack={
          <Stack gap="0.3rem">
            <Skeleton variant="rounded" height="20px" width="100%" />
            <Skeleton variant="rounded" height="20px" width="100%" />
            <Skeleton variant="rounded" height="20px" width="100%" />
          </Stack>
        }
      >
        <Stack className={classes.list}>
          <RenderWithCondition
            condition={Boolean(configList.length)}
            fallBack={
              <Typography variant="body2" color="text.secondary">
                No other calendars
              </Typography>
            }
          >
            {configList.map(configItem => (
              <Stack key={configItem.id} className={classes.configItem}>
                <Box className={classes.checkboxWrapper}>
                  <FormCheckbox
                    label={
                      <Typography variant="body2" noWrap>
                        {configItem.name}
                      </Typography>
                    }
                    disabled={isToggingView}
                    checked={configItem.isActive}
                    sx={{ color: configItem.color, '&.Mui-checked': { color: configItem.color } }}
                    onChange={async (_, checked) => {
                      await handleToggleView({ id: configItem.id, status: checked }, api => ({
                        awaitRefetchQueries: true,
                        refetchQueries: [api.sharing.queries.configuration, api.events.queries.listWithActivities],
                      }));
                    }}
                  />
                </Box>

                <ActionsMenu
                  renderTrigger={({ onClick }) => (
                    <IconButton onClick={onClick} size="small">
                      <MoreHoriz fontSize="small" />
                    </IconButton>
                  )}
                  config={[
                    {
                      label: 'Edit',
                      color: blue[500],
                      action: () => {
                        handleCloseConfig();
                        handleOpen({ id: configItem.id });
                      },
                      icon: <Edit sx={{ color: blue[500] }} fontSize="small" />,
                    },
                    {
                      label: 'Delete',
                      color: red[500],
                      disabled: isDeletingCalendarView,
                      action: async () => {
                        handleCloseConfig();
                        await handleDeleteCalendarView(configItem.id, api => ({
                          awaitRefetchQueries: true,
                          refetchQueries: [
                            api.sharing.queries.configuration,
                            api.sharing.queries.viewableUsers,
                            api.events.queries.listWithActivities,
                          ],
                        }));
                      },
                      icon: <Delete sx={{ color: red[500] }} fontSize="small" />,
                    },
                  ]}
                />
              </Stack>
            ))}
          </RenderWithCondition>
        </Stack>
      </RenderWithFallback>
    </Stack>
  );
};

const useStyles = makeStyles({
  container: {
    maxHeight: 300,
    gap: '0.5rem',
    padding: '0.5rem',
    borderRadius: '0.5rem',
    border: `1px solid ${grey[200]}`,
  },
  list: {
    flex: 1,
    gap: '0.5rem',
    overflowY: 'auto',
  },
  configItem: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  checkboxWrapper: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
});
